import React, { useEffect, useState } from "react";
import {
  Boxes, CreditCard, Store, Truck, BarChart3, ShieldCheck,
  RotateCcw, ScanLine, LayoutDashboard, MessageCircle, FileText, Users,
  Package, Zap, Check, CalendarCheck, ArrowRight, Layers,
  X, ChevronDown, Loader2, PartyPopper,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const LOGO_COLOR = "https://customer-assets.emergentagent.com/job_client-hub-266/artifacts/ysfg7n2y_Asset%209blue.png";

const CLIENT_FEATURES = [
  { icon: Zap, title: "Effortless onboarding", desc: "Branded welcome email with a 1-2-3-4 roadmap and a guided setup page — add payment, accept the agreement, connect the store." },
  { icon: LayoutDashboard, title: "Live business dashboard", desc: "Orders, shipments, units, carrier mix and trends — always current, no emails required." },
  { icon: Truck, title: "Orders & delivery tracking", desc: "Live shipment status and one-click carrier tracking with estimated delivery dates." },
  { icon: CreditCard, title: "Invoices & secure billing", desc: "Downloadable PDF invoices, one-click Pay Now, and a Stripe portal to manage their card." },
  { icon: Package, title: "Inventory & inbound POs", desc: "True on-hand stock by SKU and full visibility into what's arriving at the warehouse." },
  { icon: RotateCcw, title: "Returns & claims", desc: "See returns with photos and disposition, and file damage/loss claims with a status timeline." },
  { icon: BarChart3, title: "Reports & exports", desc: "Self-serve CSV/XLSX exports of orders, billing and fulfillment — anytime." },
  { icon: MessageCircle, title: "WhatsApp-first support", desc: "Their own support group link, right in the app. Fast, human, no tickets." },
];

const OPERATOR_FEATURES = [
  { icon: ScanLine, title: "Scanner-first receiving", desc: "iPad flow with a giant PUT-IT-HERE bin callout, smart bin logic and full scan audit logs." },
  { icon: Layers, title: "Bins & stock integrity", desc: "Excel-style bin planning, bin normalization + merge, and never-close multi-day POs." },
  { icon: RotateCcw, title: "Modern returns processing", desc: "Tap-friendly quantity entry, product photos, condition capture and suggested disposition." },
  { icon: BarChart3, title: "All-brand analytics", desc: "Returns insights, top SKUs, reasons and trends across your entire customer base." },
  { icon: CreditCard, title: "Automated billing engine", desc: "Usage-based rates per client, Stripe invoices, portals and secure card capture." },
  { icon: Boxes, title: "ShipStation reconciliation", desc: "Deduct sold units from a tracked baseline with SKU matching for a true count." },
  { icon: Users, title: "Ops cockpit & RBAC", desc: "Per-brand console, one-click act-as-client, and granular role-based permissions." },
  { icon: FileText, title: "Contracts & documents", desc: "Per-account contracts and rate cards, shareable to the client with one toggle." },
];

const INTEGRATIONS = ["ShipStation", "Stripe", "EasyPost", "Tusk", "Resend", "WhatsApp", "Barcode scanners", "Shopify · Woo · Etsy · Amazon"];

const SHOWCASE = [
  { src: "/gallery/10_dashboard.png", label: "Client dashboard", desc: "Live fulfillment analytics for every brand" },
  { src: "/gallery/11_orders_shipments.png", label: "Orders & shipments", desc: "Status and tracking at a glance" },
  { src: "/gallery/41_receiving_inventory.png", label: "Scanner-first receiving", desc: "iPad-driven put-away with bin confirmation" },
  { src: "/gallery/40_receiving_returns.png", label: "Returns & dispositions", desc: "Item photos, condition tags and where each unit goes" },
  { src: "/gallery/30_cockpit.png", label: "Operations cockpit", desc: "A pulse across your whole customer base" },
  { src: "/gallery/14_invoices.png", label: "Invoices & billing", desc: "Automated, itemized, Stripe-powered" },
];

export default function SalesFlyer() {
  const [showForm, setShowForm] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const openForm = () => setShowForm(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setShowForm(false); setLightbox(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = (showForm || lightbox) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showForm, lightbox]);

  return (
    <div className="bg-white text-bh-navy-deep pb-16 md:pb-0" data-testid="sales-flyer">
      <style>{`.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}.scrollbar-hide::-webkit-scrollbar{display:none}`}</style>

      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-bh-divider">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <img src={LOGO_COLOR} alt="BoxHero" className="h-7 sm:h-8 w-auto" />
          <button onClick={openForm} data-testid="book-walkthrough-btn"
            className="inline-flex items-center gap-2 text-sm font-bold bg-bh-orange text-bh-navy-deep rounded-full px-4 py-2 hover:bg-bh-orange-dark transition-colors">
            <CalendarCheck className="w-4 h-4" /> Book a walkthrough
          </button>
        </div>
      </div>

      {/* HERO */}
      <header className="relative overflow-hidden bg-bh-blue text-white">
        <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-bh-orange/25 blur-2xl" />
        <div aria-hidden className="absolute -bottom-32 -left-16 w-[360px] h-[360px] rounded-full bg-bh-blue-dark/60 blur-2xl" />
        <div aria-hidden className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#ffffff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-14 md:py-28">
          <p className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-black uppercase tracking-[0.16em] sm:tracking-[0.2em] text-bh-orange">
            <Zap className="w-4 h-4" fill="currentColor" /> The OS for modern 3PLs
          </p>
          <h1 className="mt-5 text-white font-black leading-[0.98] tracking-tight text-[2.6rem] sm:text-6xl md:text-7xl max-w-4xl" style={{ fontFamily: "Manrope" }}>
            Run your warehouse.<br />
            <span className="inline-block mt-2 sm:mt-3 bg-bh-orange text-bh-navy-deep px-2.5 sm:px-3 py-0.5 rounded-lg -rotate-1">Delight your brands.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            One branded portal: a scanner-first command center for your <strong className="text-white font-semibold">operations team</strong>, and a beautiful self-serve dashboard for your <strong className="text-white font-semibold">brand customers</strong>.
          </p>
          <div className="mt-8">
            <button onClick={openForm}
              className="inline-flex items-center justify-center gap-2 font-bold bg-bh-orange text-bh-navy-deep rounded-full px-7 py-4 hover:bg-bh-orange-dark transition-colors shadow-lg shadow-bh-navy-deep/20">
              Book a walkthrough <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-8 sm:mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-white/80">
            {["White-label ready", "iPad / scanner-first", "Stripe billing built in"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-bh-orange text-bh-navy-deep flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>{t}
              </span>
            ))}
          </div>
        </div>
        <div aria-hidden className="relative h-2 bg-bh-orange" />
      </header>

      {/* WHY */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <WhyCard accent="blue" icon={Layers} title="Two audiences, one truth" desc="Operators and clients see the same data through purpose-built views. No double entry, no chasing answers." />
          <WhyCard accent="orange" icon={Zap} title="Onboarding that closes" desc="New clients go from signed contract to live dashboard in minutes with guided, self-serve setup." />
          <WhyCard accent="blue" icon={ShieldCheck} title="Money handled" desc="Automated usage-based billing, invoices, and secure Stripe card capture — you never touch card data." />
        </div>
      </section>

      {/* TWO COLUMNS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7 items-start">
          <FeatureColumn accent="blue" eyebrow="For your brand clients"
            title="A premium customer experience"
            blurb="Make your 3PL look like a tech company, not just a warehouse."
            features={CLIENT_FEATURES} testId="client-column" />
          <FeatureColumn accent="orange" eyebrow="For your operations team"
            title="Tools your floor actually needs"
            blurb="Fast, accurate and auditable — from dock to dispatch."
            features={OPERATOR_FEATURES} testId="operator-column" />
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="bg-bh-bg border-y border-bh-divider">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-12 md:py-16">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-bh-blue">See it in action</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-bh-navy-deep mt-2 max-w-2xl" style={{ fontFamily: "Manrope" }}>
            One platform, <span className="text-bh-orange">every workflow</span>
          </h2>
          <p className="text-bh-body mt-3 max-w-xl text-sm">
            From a brand's live dashboard to the warehouse floor — the screens your customers and your team use every day.
            <span className="md:hidden font-semibold text-bh-blue"> Swipe to browse →</span>
          </p>
          <div className="mt-8 flex md:grid md:grid-cols-3 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-5 px-5 md:mx-0 md:px-0 pb-4 md:pb-0 scrollbar-hide">
            {SHOWCASE.map((s) => <BrowserFrame key={s.src} {...s} onOpen={() => setLightbox(s)} />)}
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-12 md:py-16">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-bh-orange">Already wired in</p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-bh-navy-deep mt-2" style={{ fontFamily: "Manrope" }}>
          Connected to the tools you run
        </h2>
        <div className="mt-7 flex flex-wrap gap-3">
          {INTEGRATIONS.map((i, idx) => (
            <span key={i} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold border-2 ${idx % 2 ? "border-bh-orange/30 text-bh-navy bg-bh-orange/5" : "border-bh-blue/25 text-bh-navy bg-bh-blue/5"}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${idx % 2 ? "bg-bh-orange" : "bg-bh-blue"}`} /> {i}
            </span>
          ))}
        </div>
      </section>

      {/* WHITE-LABEL CTA */}
      <section className="px-5 sm:px-6 pb-16">
        <div className="max-w-6xl mx-auto rounded-[28px] bg-bh-blue text-white px-6 sm:px-10 md:px-14 py-12 md:py-14 relative overflow-hidden">
          <div aria-hidden className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-bh-orange/25 blur-2xl" />
          <div className="relative">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-bh-orange">White-label &amp; resell</p>
            <h2 className="mt-3 text-white text-3xl sm:text-4xl md:text-5xl font-black max-w-2xl leading-[1.02]" style={{ fontFamily: "Manrope" }}>
              Your brand. Your domain. <span className="text-bh-orange">Your pricing.</span>
            </h2>
            <p className="mt-5 text-white/85 max-w-2xl text-base sm:text-lg leading-relaxed">
              Offer your brands a modern portal your competitors can't — and turn "we use spreadsheets"
              into "we have our own software."
            </p>
            <button onClick={openForm}
              className="mt-8 inline-flex items-center gap-2 font-bold bg-bh-orange text-bh-navy-deep rounded-full px-7 py-4 hover:bg-bh-orange-dark transition-colors">
              Get your logo on it <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t-2 border-bh-orange/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_COLOR} alt="BoxHero" className="h-9 w-auto" />
          <p className="text-sm text-bh-muted text-center sm:text-right">
            Your Brand's Fulfillment Partner · Brooklyn, NY<br />
            <a href="mailto:3pl@boxhero.com" className="text-bh-blue font-semibold">3pl@boxhero.com</a> · (929) 900-1101 · boxhero.com
          </p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-bh-divider px-4 py-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <button onClick={openForm} data-testid="mobile-cta"
          className="w-full inline-flex items-center justify-center gap-2 font-bold bg-bh-orange text-bh-navy-deep rounded-full py-3.5 hover:bg-bh-orange-dark transition-colors">
          <CalendarCheck className="w-4 h-4" /> Book a walkthrough
        </button>
      </div>

      {showForm && <LeadModal onClose={() => setShowForm(false)} />}
      {lightbox && <Lightbox shot={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function WhyCard({ accent, icon: Icon, title, desc }) {
  const isBlue = accent === "blue";
  return (
    <div className="group rounded-2xl bg-white border border-bh-divider p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 overflow-hidden relative">
      <div className={`absolute top-0 left-0 h-1.5 w-full ${isBlue ? "bg-bh-blue" : "bg-bh-orange"}`} />
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isBlue ? "bg-bh-blue/10 text-bh-blue" : "bg-bh-orange/15 text-bh-orange-dark"}`}>
        <Icon className="w-6 h-6" strokeWidth={2.2} />
      </div>
      <h3 className="mt-4 text-lg font-extrabold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{title}</h3>
      <p className="mt-1.5 text-sm text-bh-body leading-relaxed">{desc}</p>
    </div>
  );
}

function FeatureColumn({ accent, eyebrow, title, blurb, features, testId }) {
  const isBlue = accent === "blue";
  const band = isBlue ? "bg-bh-blue" : "bg-bh-orange";
  const titleColor = isBlue ? "text-white" : "text-bh-navy-deep";
  const eyebrowColor = isBlue ? "text-white/80" : "text-bh-navy-deep/70";
  const blurbColor = isBlue ? "text-white/85" : "text-bh-navy-deep/75";
  const chip = isBlue ? "bg-bh-blue/10 text-bh-blue" : "bg-bh-orange/15 text-bh-orange-dark";
  return (
    <div data-testid={testId} className="rounded-3xl overflow-hidden border border-bh-divider bg-white shadow-sm">
      <div className={`${band} px-6 sm:px-8 py-6 sm:py-7`}>
        <p className={`text-xs font-black uppercase tracking-[0.16em] ${eyebrowColor}`}>{eyebrow}</p>
        <h2 className={`mt-1.5 text-2xl md:text-3xl font-black ${titleColor}`} style={{ fontFamily: "Manrope" }}>{title}</h2>
        <p className={`mt-2 text-sm font-medium ${blurbColor}`}>{blurb}</p>
      </div>
      <div className="p-5 sm:p-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-2 md:gap-1">
        {features.map((f) => <FeatureItem key={f.title} f={f} chip={chip} />)}
      </div>
    </div>
  );
}

function FeatureItem({ f, chip }) {
  const [open, setOpen] = useState(false);
  const Icon = f.icon;
  return (
    <div className="border-b border-bh-divider/60 last:border-0 md:border-0">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 text-left py-3 md:py-2 md:cursor-default">
        <div className={`shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center ${chip}`}>
          <Icon className="w-5 h-5" strokeWidth={2.1} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-extrabold text-[14px] md:text-[15px] leading-tight text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{f.title}</p>
            <ChevronDown className={`md:hidden w-4 h-4 text-bh-muted shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
          <p className={`${open ? "block" : "hidden"} md:block text-sm leading-relaxed text-bh-body mt-1`}>{f.desc}</p>
        </div>
      </button>
    </div>
  );
}

function BrowserFrame({ src, label, desc, onOpen }) {
  return (
    <figure className="group snap-center shrink-0 w-[80%] sm:w-[58%] md:w-auto rounded-xl overflow-hidden border border-bh-divider bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-bh-blue">
        <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-bh-orange" />
      </div>
      <button type="button" onClick={onOpen} data-testid="showcase-shot" className="block w-full aspect-[16/10] overflow-hidden bg-bh-bg cursor-zoom-in">
        <img src={src} alt={label} loading="lazy"
          className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
      </button>
      <figcaption className="px-4 py-3 border-t border-bh-divider">
        <p className="text-sm font-extrabold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{label}</p>
        <p className="text-xs text-bh-muted mt-0.5">{desc}</p>
      </figcaption>
    </figure>
  );
}

function Lightbox({ shot, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-bh-navy-deep/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Close">
        <X className="w-5 h-5" />
      </button>
      <figure className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={shot.src} alt={shot.label} className="max-h-[80vh] w-auto max-w-full object-contain rounded-xl shadow-2xl border border-white/10 bg-white" />
        <figcaption className="text-center mt-4">
          <p className="text-white font-bold" style={{ fontFamily: "Manrope" }}>{shot.label}</p>
          <p className="text-white/60 text-sm mt-0.5">{shot.desc}</p>
        </figcaption>
      </figure>
    </div>
  );
}

const PLATFORMS = ["Shopify", "WooCommerce", "Etsy", "Amazon", "Other", "Not sure yet"];

function LeadModal({ onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", platform: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) {
      setError("Please add your name, company and email."); return;
    }
    setBusy(true); setError("");
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err) || "Something went wrong. Please email 3pl@boxhero.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bh-navy-deep/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="lead-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-bh-blue px-6 py-5 flex items-start justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.16em] text-bh-orange">Let's talk</p>
            <h3 className="text-white text-xl font-black mt-1" style={{ fontFamily: "Manrope" }}>Book a walkthrough</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" aria-label="Close" data-testid="lead-close"><X className="w-6 h-6" /></button>
        </div>

        {done ? (
          <div className="px-6 py-12 text-center" data-testid="lead-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><PartyPopper className="w-7 h-7" /></div>
            <h4 className="text-lg font-black text-bh-navy-deep mt-4" style={{ fontFamily: "Manrope" }}>Thanks — you're on our list!</h4>
            <p className="text-bh-body text-sm mt-2 max-w-xs mx-auto">We'll be in touch within one business day to set up your walkthrough.</p>
            <button onClick={onClose} className="mt-6 inline-flex items-center gap-2 font-bold bg-bh-navy-deep text-white rounded-full px-6 py-3 hover:brightness-125 transition">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <p className="text-sm text-bh-body">Tell us a little about your operation and we'll reach out to schedule a demo — no pressure.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LField label="Your name *" testId="lead-name" value={form.name} onChange={on("name")} placeholder="Jane Rivera" />
              <LField label="Company *" testId="lead-company" value={form.company} onChange={on("company")} placeholder="Acme 3PL" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LField label="Work email *" testId="lead-email" type="email" value={form.email} onChange={on("email")} placeholder="jane@acme3pl.com" />
              <LField label="Phone" testId="lead-phone" value={form.phone} onChange={on("phone")} placeholder="(555) 123-4567" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-bh-muted mb-1.5">Which platforms do your brands use?</label>
              <select data-testid="lead-platform" value={form.platform} onChange={on("platform")}
                className="w-full px-3 py-2.5 border border-bh-divider rounded-lg bg-white text-sm text-bh-navy-deep focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue">
                <option value="">Select one…</option>
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-bh-muted mb-1.5">Anything you'd like us to know?</label>
              <textarea data-testid="lead-message" value={form.message} onChange={on("message")} rows={3} placeholder="How many brands do you serve? What are you using today?"
                className="w-full px-3 py-2.5 border border-bh-divider rounded-lg bg-white text-sm text-bh-navy-deep focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue resize-none" />
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="lead-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="lead-submit"
              className="w-full inline-flex items-center justify-center gap-2 font-bold bg-bh-orange text-bh-navy-deep rounded-full py-3.5 hover:bg-bh-orange-dark disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />}
              {busy ? "Sending…" : "Request my walkthrough"}
            </button>
            <p className="text-[11px] text-bh-muted text-center">We'll only use your details to contact you about BoxHero.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function LField({ label, testId, type = "text", ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-bh-muted mb-1.5">{label}</label>
      <input data-testid={testId} type={type} {...props}
        className="w-full px-3 py-2.5 border border-bh-divider rounded-lg bg-white text-sm text-bh-navy-deep focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue" />
    </div>
  );
}
